import {
  trans, WEBHOOKS_FILTERS_MATCH_EXPLAINER, WEBHOOKS_FILTERS_MATCH_EXPLAINER_MULTI_FIELD, WEBHOOKS_FILTERS_NO_OPTIONS_AVAILABLE,
  WEBHOOKS_FILTERS_PLACEHOLDER_ANY, WEBHOOKS_FILTERS_VALUE_UNAVAILABLE_HINT,
  WEBHOOKS_FILTERS_FIELD_NEEDS_ATTENTION, WEBHOOKS_FILTERS_VALUE_DELETED_HINT,
  WEBHOOKS_FILTERS_VALUE_UNAVAILABLE_SUFFIX, WEBHOOKS_FILTERS_GROUP_BADGE,
  WEBHOOKS_FILTERS_SUB_GROUP_BADGE, WEBHOOKS_FILTERS_GROUP_INCLUDES_DESCENDANTS
} from '../../translator'
import {
  EventFilters, FilterOption, IssueFilterOptions, fieldsFor,
  missingSelections, optionByIdForEvent, selectableOptions
} from './webhook-filter-options'
import { fieldLabel } from './webhook-filter-formatters'

interface PanelContext {
  filters: EventFilters
  options: IssueFilterOptions
}

export function buildPanel (eventType: string, context: PanelContext): DocumentFragment {
  const fragment = document.createDocumentFragment()
  const fields = fieldsFor(eventType)
  const explainer = document.createElement('p')
  explainer.className = 'text-muted small mb-3'
  explainer.textContent = trans(fields.length > 1 ? WEBHOOKS_FILTERS_MATCH_EXPLAINER_MULTI_FIELD : WEBHOOKS_FILTERS_MATCH_EXPLAINER, {}, 'integrations')
  fragment.appendChild(explainer)
  fields.forEach((field) => fragment.appendChild(buildField(eventType, field, context)))
  return fragment
}

function buildField (eventType: string, field: string, context: PanelContext): HTMLElement {
  const wrapper = document.createElement('div')
  wrapper.className = 'webhook-filter-field form-group mb-3'
  wrapper.dataset.field = field

  const label = document.createElement('label')
  label.className = 'form-control-label d-block mb-1'
  label.textContent = fieldLabel(field)
  wrapper.appendChild(label)

  const options = selectableOptions(context.filters, context.options, eventType, field)
  if (options.length === 0) {
    const empty = document.createElement('p')
    empty.className = 'text-muted small mb-0'
    empty.textContent = trans(WEBHOOKS_FILTERS_NO_OPTIONS_AVAILABLE, {}, 'integrations')
    wrapper.appendChild(empty)
    return wrapper
  }

  const dropdown = document.createElement('div')
  dropdown.className = 'ui fluid multiple search selection dropdown webhook-filter-dropdown'
  dropdown.dataset.field = field
  dropdown.dataset.eventType = eventType
  const hidden = document.createElement('input')
  hidden.type = 'hidden'
  dropdown.appendChild(hidden)
  const icon = document.createElement('i')
  icon.className = 'dropdown icon'
  dropdown.appendChild(icon)
  const placeholder = document.createElement('div')
  placeholder.className = 'default text'
  placeholder.textContent = trans(WEBHOOKS_FILTERS_PLACEHOLDER_ANY, { '%field%': fieldLabel(field).toLowerCase() }, 'integrations')
  dropdown.appendChild(placeholder)
  const menu = document.createElement('div')
  menu.className = 'menu'
  options.forEach((option) => menu.appendChild(buildMenuItem(option)))
  dropdown.appendChild(menu)
  wrapper.appendChild(dropdown)

  const selected = context.filters[eventType]?.[field] ?? []
  const unavailableNames = selected
    .map((selection) => optionByIdForEvent(context.options, field, selection.id, eventType))
    .filter((option): option is FilterOption => option !== undefined && !option.available)
    .map((option) => option.path)
  if (unavailableNames.length > 0) {
    wrapper.appendChild(buildHint(trans(WEBHOOKS_FILTERS_VALUE_UNAVAILABLE_HINT, { '%name%': unavailableNames.join(', ') }, 'integrations'), 'text-muted'))
  }

  if (missingSelections(context.filters, context.options, eventType, field).length > 0) {
    wrapper.classList.add('webhook-filter-field-needs-attention')
    const badge = document.createElement('span')
    badge.className = 'badge badge-warning ml-2'
    badge.textContent = trans(WEBHOOKS_FILTERS_FIELD_NEEDS_ATTENTION, {}, 'integrations')
    label.appendChild(badge)
    wrapper.appendChild(buildHint(trans(WEBHOOKS_FILTERS_VALUE_DELETED_HINT, {}, 'integrations'), 'text-warning'))
  }
  return wrapper
}

function buildMenuItem (option: FilterOption): HTMLElement {
  const item = document.createElement('div')
  item.className = 'item'
  item.dataset.value = option.id
  item.dataset.kind = option.kind
  item.dataset.name = option.name
  item.dataset.text = option.path
  if (option.parent_id !== null) item.dataset.parentId = option.parent_id
  item.textContent = option.available
    ? option.path
    : trans(WEBHOOKS_FILTERS_VALUE_UNAVAILABLE_SUFFIX, { '%name%': option.path }, 'integrations')
  if (!option.available) {
    item.classList.add('disabled')
    item.setAttribute('aria-disabled', 'true')
  }
  if (option.kind === 'group') {
    const badge = document.createElement('span')
    badge.className = 'ui mini label ml-2 webhook-filter-group-badge'
    badge.textContent = trans(option.parent_id === null ? WEBHOOKS_FILTERS_GROUP_BADGE : WEBHOOKS_FILTERS_SUB_GROUP_BADGE, {}, 'integrations')
    badge.title = trans(WEBHOOKS_FILTERS_GROUP_INCLUDES_DESCENDANTS, {}, 'integrations')
    item.appendChild(badge)
  }
  return item
}

function buildHint (text: string, className: string): HTMLElement {
  const hint = document.createElement('p')
  hint.className = `small mb-0 mt-1 ${className}`
  hint.textContent = text
  return hint
}
